import { z } from 'zod';
import type {
  FulfillmentCandidatesResponse,
  OverrideOptionInput,
} from '@erp/rpc';
import type {
  AssignmentLine,
  CartSummary,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

export type VendorCandidate = FulfillmentCandidatesResponse['vendors'][number];

export type VendorCandidateSort = 'cost' | 'lead_time';

/**
 * Where a buy from the vendor ships and how the goods reach the destination:
 * direct, or bought into the pivot and transferred on from there (GEN-3246).
 * A vendor from before PR 1's ship-to fields ships to the destination.
 */
export function vendorBuyRouting(
  vendor: Partial<
    Pick<VendorCandidate, 'shipToWarehouseId' | 'pivotsThroughWarehouseId'>
  >,
  destinationWarehouseId: string,
): {
  action: 'buy' | 'buy_and_transfer';
  shipToWarehouseId: string;
  sourceWarehouseId: string | null;
} {
  const pivot = vendor.pivotsThroughWarehouseId ?? null;
  if (pivot !== null) {
    return {
      action: 'buy_and_transfer',
      shipToWarehouseId: pivot,
      sourceWarehouseId: pivot,
    };
  }
  return {
    action: 'buy',
    shipToWarehouseId: vendor.shipToWarehouseId ?? destinationWarehouseId,
    sourceWarehouseId: null,
  };
}

/** The override that buys the line's quantity from the vendor. */
export function vendorBuyOverrideInput(
  line: { suggestionId: string; destinationWarehouseId: string },
  vendor: Partial<
    Pick<VendorCandidate, 'shipToWarehouseId' | 'pivotsThroughWarehouseId'>
  > & { supplierId: string; unitPrice: number | null },
  quantity: number,
): OverrideOptionInput {
  const routing = vendorBuyRouting(vendor, line.destinationWarehouseId);
  return {
    suggestionId: line.suggestionId,
    action: routing.action,
    supplierId: vendor.supplierId,
    shipToWarehouseId: routing.shipToWarehouseId,
    buyQuantity: quantity,
    unitCost: vendor.unitPrice,
    sourceWarehouseId: routing.sourceWarehouseId,
    transferQuantity: routing.sourceWarehouseId === null ? null : quantity,
    setChosen: true,
  };
}

function sortKey(vendor: VendorCandidate, sort: VendorCandidateSort): number {
  if (sort === 'lead_time') return vendor.leadTime.days ?? Infinity;
  return vendor.unitPrice ?? Infinity;
}

/**
 * The current vendor first, then priced vendors by the chosen key (unknown
 * last), then the unpriced ones; ties go to the cheaper, then by name.
 */
export function sortVendorCandidates(
  vendors: readonly VendorCandidate[],
  currentSupplierId: string | null | undefined,
  sort: VendorCandidateSort,
): VendorCandidate[] {
  const rank = (vendor: VendorCandidate) => {
    if (vendor.supplierId === currentSupplierId) return 0;
    return vendor.priced ? 1 : 2;
  };
  return [...vendors].sort(
    (left, right) =>
      rank(left) - rank(right) ||
      sortKey(left, sort) - sortKey(right, sort) ||
      (left.unitPrice ?? Infinity) - (right.unitPrice ?? Infinity) ||
      left.supplierName.localeCompare(right.supplierName),
  );
}

/** The line's extended cost at the vendor: its quote times the quoted qty. */
export function vendorExtendedCost(vendor: VendorCandidate): number | null {
  if (vendor.unitPrice == null || vendor.quotedQuantity == null) return null;
  return vendor.unitPrice * vendor.quotedQuantity;
}

export type VendorCartStatus =
  /** The board has the vendor's cart loaded: the MOV gap with the line in it. */
  | { cart: 'loaded'; shortOfMov: number | null }
  /**
   * The cart isn't among those the board loaded (another page or filter, or
   * none yet), so its total is unknown: show the MOV alone, no gap.
   */
  | { cart: 'unknown'; movThreshold: number | null };

/** The vendor's cart at the ship-to once the line joins it. */
export function vendorCartStatus(
  cart: Pick<CartSummary, 'cartTotal' | 'movThreshold'> | undefined,
  movThreshold: number | null,
  addedCost: number | null,
): VendorCartStatus {
  if (cart === undefined) return { cart: 'unknown', movThreshold };
  const threshold = cart.movThreshold ?? movThreshold;
  const total = cart.cartTotal + (addedCost ?? 0);
  return {
    cart: 'loaded',
    shortOfMov: threshold == null ? null : Math.max(0, threshold - total),
  };
}

type LoadedCart = {
  supplierId: string | null;
  shipToWarehouseId: string | null;
  cartTotal: number;
  movThreshold: number | null;
};

type CartsSummaryRows = {
  rows: ReadonlyArray<{
    supplierId: string;
    carts: ReadonlyArray<
      Pick<CartSummary, 'shipToWarehouseId' | 'cartTotal' | 'movThreshold'>
    >;
  }>;
};

/**
 * Every vendor cart the board has loaded: the buying board's order rows
 * (one per vendor and ship-to; vendor-grouped rows have no ship-to) and any
 * cart summaries.
 */
export function loadedCarts(
  buyingOrders: ReadonlyArray<{ rows: readonly LoadedCart[] } | undefined>,
  summaries: ReadonlyArray<CartsSummaryRows | undefined>,
): LoadedCart[] {
  return [
    ...buyingOrders.flatMap((page) => page?.rows ?? []),
    ...summaries.flatMap((summary) =>
      (summary?.rows ?? []).flatMap((row) =>
        row.carts.map((cart) => ({ ...cart, supplierId: row.supplierId })),
      ),
    ),
  ];
}

/**
 * Buying-board filters that drop lines before grouping, so a row's
 * `cartTotal` covers only the matching lines (listBuyingOrders.ts).
 */
const LineFiltersSchema = z.object({
  buyerCodes: z.array(z.string()).optional(),
  lineTypes: z.array(z.string()).optional(),
  popularityCodes: z.array(z.string()).optional(),
  stockStatuses: z.array(z.string()).optional(),
  warehouseTypes: z.array(z.string()).optional(),
  skuSearch: z.string().optional(),
  textSearch: z.string().optional(),
});

const QueryKeyOptionsSchema = z.object({ input: z.unknown() });

/** The input an oRPC query key was made from, if it carries one. */
export function queryInputOf(queryKey: readonly unknown[]): unknown {
  const options = QueryKeyOptionsSchema.safeParse(queryKey[1]);
  return options.success ? options.data.input : undefined;
}

function isUnsetFilter(value: string | string[] | undefined): boolean {
  if (value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return value.length === 0;
}

/** A buying-orders query whose rows carry whole-cart totals. */
export function isWholeCartQuery(input: unknown): boolean {
  if (typeof input !== 'object' || input === null) return false;
  const filters = LineFiltersSchema.safeParse(input);
  if (!filters.success) return false;
  return Object.values(filters.data).every(isUnsetFilter);
}

/** The vendor's cart at the ship-to among the loaded carts. */
export function findLoadedCart(
  carts: readonly LoadedCart[],
  supplierId: string,
  shipToWarehouseId: string,
): Pick<CartSummary, 'cartTotal' | 'movThreshold'> | undefined {
  return carts.find(
    (cart) =>
      cart.supplierId === supplierId &&
      cart.shipToWarehouseId === shipToWarehouseId,
  );
}

/** A pending buy line with a SKU can move to another vendor (GEN-3246). */
export function canSwitchVendor(line: AssignmentLine): boolean {
  return (
    line.status === 'pending' &&
    line.approvalState !== 'released' &&
    line.productV3Key != null &&
    isSingleVendorBuy(line)
  );
}

/**
 * A plain buy, or a buy into a hub that transfers all of it on. A split plan
 * (some units transferred from stock, the rest bought) is not: replacing it
 * with a vendor buy would drop the transfer and leave those units uncovered.
 */
function isSingleVendorBuy(line: AssignmentLine): boolean {
  const option = line.topOption;
  if (option?.action === 'buy') return true;
  return (
    option?.action === 'buy_and_transfer' &&
    option.sourceWarehouseId != null &&
    option.sourceWarehouseId === line.shipToWarehouseId &&
    option.transferQuantity === option.buyQuantity
  );
}
